import React from 'react';
import { PlotUtilities } from './renderers/types';
import { GenericDataSet } from './types';
import { exists, findClosest } from './utilities/utils';
import { useScaleFactor } from './utilities/useScaleFactor';

export type PinValue = {
    time: number;
    isActive: boolean;
};

type Props = {
    height: number;
    x: number;
    y?: number;
    onPinHeadClick?: () => void;
    isActive: boolean;
};

export const Pin: React.FC<Props> = ({ height, x, y, onPinHeadClick, isActive }) => {
    const onPinHeadClickInternal = (): void => {
        if (isActive && exists(onPinHeadClick)) {
            onPinHeadClick();
        }
    };

    const pinOuterRadius = 10;
    const pinInnerRadius = 7;
    const closeIconSize = 6;

    return (
        <svg className="pin" data-test="viz-pin">
            <g transform={`translate(${x}, ${y})`}>
                <line className="pin-line" x1={0} y1={0} x2={0} y2={height} />
                <circle
                    className={`pin-dot-inner ${isActive ? '' : 'disabled'}`}
                    r={pinInnerRadius}
                    cx={0}
                    cy={0}
                    onClick={onPinHeadClickInternal}
                />
                {isActive && (
                    <>
                        <circle
                            className="pin-dot-outer"
                            data-test="pin-dot-circle"
                            r={pinOuterRadius}
                            cx={0}
                            cy={0}
                            onClick={onPinHeadClickInternal}
                        />
                        <g transform={`translate(-${closeIconSize / 2}, -${closeIconSize / 2})`}>
                            <line
                                className="pin-close-line"
                                x1={0}
                                y1={0}
                                x2={closeIconSize}
                                y2={closeIconSize}
                            />
                            <line
                                className="pin-close-line"
                                x1={0}
                                y1={closeIconSize}
                                x2={closeIconSize}
                                y2={0}
                            />
                        </g>
                    </>
                )}
            </g>
        </svg>
    );
};

type PinContainerProps = {
    plotUtilities?: PlotUtilities;
    data: GenericDataSet;
    pinValue?: PinValue;
    onPinClose?: () => void;
};

export const PinContainer: React.FC<PinContainerProps> = props => {
    const { plotUtilities, data, pinValue, onPinClose } = props;
    const scaleFactor = useScaleFactor();

    if (!pinValue || !plotUtilities) {
        return null;
    }

    // Compute offset of the "u-over" from the "u-root", and height of pin
    const { height: overHeight } = plotUtilities.dimensions.over;
    const scaledOverHeight = overHeight / scaleFactor;

    // Compute pin x; handle "null" data
    const timeData = data.columns ? (data.columns[0] as number[]) : null;
    const xValue = pinValue.time;
    const closestXValue = timeData ? findClosest(timeData, xValue) : null;

    if (
        closestXValue === null ||
        (timeData && closestXValue < timeData[0]) ||
        (timeData && closestXValue > timeData[timeData.length - 1])
    ) {
        return null;
    }
    const pinX = plotUtilities.valToPos(closestXValue, 'x');

    return (
        <Pin
            x={pinX}
            y={0}
            height={scaledOverHeight}
            onPinHeadClick={onPinClose}
            isActive={pinValue.isActive}
        />
    );
};
